import { useEffect } from "react";

import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { ReferalDoctorSearchAutocompleteCustom } from "@/Common/Components/Search/ReferalDoctorSearchAutocompleteCustom";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { ContactDirectoryClient } from "@/ContactDirectory/Api/ContactDirectoryClient";
import { FaxDirectorySearchResultItemMapper } from "@/ContactDirectory/Api/Mapper/FaxDirectorySearchResultItemMapper";
import { FaxDirectorySearchComponent } from "@/ContactDirectory/Components/Search/FaxDirectorySearchComponent";
import { FaxDirectorySearchResultItem } from "@/ContactDirectory/Components/Search/FaxDirectorySearchResultItem";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";

import styles from "./MedicalRequestFormRecipientField.module.scss";

interface IMedicalRequestFormRecipient {
    model: IMedicalRequest;
    disabled: boolean;
    onRecipientChange: (recipient: string) => void;
    onRecipientSearchTextChange: (searchText: string) => void;
    onContactDirectoryEntityChanged: (selection: DirectorySelectableEntity<IDirectoryEntity>) => void;
    onClearRecipient: () => void;
}

export function MedicalRequestFormRecipientField(props: Readonly<IMedicalRequestFormRecipient>): JSX.Element {
    const medicalRequest = props.model;
    const inputId = "add_appt_referal";

    const removeSelection = (): void => {
        props.onClearRecipient();
        window.setTimeout(() => $("#" + inputId)?.focus(), 100);
    };

    useEffect(
        () => {
            if (!props.model.recipientContactDirectoryEntityId)
                return;

            if (props.model.recipientContactDirectoryEntityId !== props.model.recipientDirectorySelectedEntity?.id) {
                const loadContactDirectoryEntityDetails = async (): Promise<void> => {
                    const result = await ContactDirectoryClient
                        .getInstance()
                        .getSelectableEntityById(
                            props.model.recipientContactDirectoryEntityType,
                            props.model.recipientContactDirectoryEntityId
                        );

                    if (result != null)
                        props.onContactDirectoryEntityChanged(result);
                };

                loadContactDirectoryEntityDetails();
            }
        },
        [props.model.recipientContactDirectoryEntityId, props]
    );

    if (props.model.recipientDirectorySelectedEntity) {
        const displayName = props.model?.recipientDirectorySelectedEntity.model.getDisplayName();

        return (
            <div className={styles.selectedRecipient}>
                <span>{displayName}</span>
                {!props.disabled && (
                    <button className={styles.clearRecipient} onClick={removeSelection}>X</button>
                )}
            </div>
        );
    }

    return (
        <FeatureFlagControl
            flag={cacheStore.ClinicSettings.EnableContactDirectory}
            activeFeature={(
                <FaxDirectorySearchComponent
                    componentId={inputId}
                    isValid={true}
                    disableEdit={true}
                    onItemSelected={(selection: FaxDirectorySearchResultItem) => {
                        props.onContactDirectoryEntityChanged(FaxDirectorySearchResultItemMapper.toDirectorySelectableEntity(selection));
                    }}
                    searchText={medicalRequest.recipientSearchText ?? medicalRequest.recipient}
                    onSearchTextChanged={textValue => props.onRecipientSearchTextChange?.(textValue)}
                    clearSearchTextOnSelect={true}
                    dataCy="contactDirectory-consultationRequestRecipientSearch"
                    allowExpandingGeographicSearch
                />
            )}
            fallbackFeature={(
                <ReferalDoctorSearchAutocompleteCustom
                    useblurevent={true}
                    disabled={props.disabled}
                    componentId={inputId}
                    selectedDoctors={[].toImmutable()}
                    selectionCallBack={(personDropDown: PersonDropdownItem) => props.onRecipientChange?.(personDropDown.Item.formattedNameLicencePreferredPhone)}
                    changedTextValueCallback={textValue => props.onRecipientSearchTextChange?.(textValue)}
                    closeDropDownAfterSelection={true}
                    defaultTextValue={medicalRequest.recipientSearchText}
                    style={{ height: "37px" }}
                />
            )}
        />
    );
}
